import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { orderAPI } from '../../services/api'
import { formatEuro, toNumber } from '../../utils/format'
import AppIcon from '../../components/public/AppIcon'

import { logger } from '@/utils/logger';
export default function Checkout() {
  const location = useLocation()
  const navigate = useNavigate()

  const { app, plan, billingCycle } = location.state || {}

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirm: '',
    company_name: '',
    contact_person: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Deutschland',
    accept_terms: false
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [validationErrors, setValidationErrors] = useState({})

  // Ohne App-/Tarifdaten zurück zur Übersicht
  useEffect(() => {
    if (!app || !plan) {
      navigate('/apps')
    }
  }, [app, plan, navigate])

  if (!app || !plan) {
    return null
  }

  const monthly = toNumber(plan.price_monthly)
  const yearly = toNumber(plan.price_yearly)
  const price = billingCycle === 'YEARLY' ? yearly : monthly
  const savings = monthly * 12 - yearly
  const savingsPercent = monthly > 0 ? Math.round((savings / (monthly * 12)) * 100) : 0

  const validateForm = () => {
    const errors = {}

    if (!formData.email) {
      errors.email = 'E-Mail ist erforderlich'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Ungültige E-Mail-Adresse'
    }

    if (!formData.password) {
      errors.password = 'Passwort ist erforderlich'
    } else if (formData.password.length < 8) {
      errors.password = 'Passwort muss mindestens 8 Zeichen lang sein'
    }

    if (formData.password !== formData.password_confirm) {
      errors.password_confirm = 'Passwörter stimmen nicht überein'
    }

    if (!formData.company_name) {
      errors.company_name = 'Firmenname ist erforderlich'
    }

    if (!formData.contact_person) {
      errors.contact_person = 'Ansprechpartner ist erforderlich'
    }

    if (!formData.accept_terms) {
      errors.accept_terms = 'Sie müssen die AGB akzeptieren'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      const orderData = {
        app_id: app.id,
        plan_id: plan.id,
        billing_cycle: billingCycle,
        customer_data: {
          email: formData.email,
          password: formData.password,
          company_name: formData.company_name,
          contact_person: formData.contact_person,
          phone: formData.phone || null,
          address: formData.address || null,
          city: formData.city || null,
          postal_code: formData.postal_code || null,
          country: formData.country
        }
      }

      const response = await orderAPI.create(orderData)

      // Bestellung erfolgreich – weiter zur Bestätigungsseite
      navigate('/checkout/success', {
        state: {
          orderId: response.data.order_id,
          email: formData.email,
          appName: app.name
        }
      })
    } catch (err) {
      logger.error('Order error:', err)
      setError(err.response?.data?.detail || 'Fehler bei der Bestellung. Bitte versuchen Sie es erneut.')
    } finally {
      setLoading(false)
    }
  }

  const parseFeatureList = (jsonString) => {
    try {
      return JSON.parse(jsonString || '[]')
    } catch {
      return []
    }
  }

  const setField = (name, value) => setFormData({ ...formData, [name]: value })

  const renderField = ({ name, label, type = 'text', required = false, autoComplete = undefined }) => (
    <div className="wc-field">
      <label htmlFor={`co-${name}`}>
        {label} {required && '*'}
      </label>
      <input
        id={`co-${name}`}
        type={type}
        className="wc-input"
        value={formData[name]}
        onChange={(e) => setField(name, e.target.value)}
        required={required}
        autoComplete={autoComplete}
        aria-invalid={validationErrors[name] ? 'true' : undefined}
        style={
          validationErrors[name]
            ? { borderColor: '#fca5a5' }
            : undefined
        }
      />
      {validationErrors[name] && (
        <p style={{ color: '#fca5a5', fontSize: 12, margin: '4px 0 0' }}>
          {validationErrors[name]}
        </p>
      )}
    </div>
  )

  return (
    <div className="wc-container" style={{ padding: '64px 32px' }}>
      <p className="wc-kicker">Bestellung</p>
      <h1 style={{ marginBottom: 40 }}>Bestellung abschließen</h1>

      <div
        className="wc-split-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: '1.6fr 1fr',
          gap: 32,
          alignItems: 'start',
        }}
      >
        {/* Formular */}
        <div className="wc-card wc-elev-sm" style={{ padding: 24, gap: 24 }}>
          <h2 style={{ fontSize: 25, margin: 0 }}>Ihre Daten</h2>

          {error && (
            <div className="wc-alert wc-alert-error" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 28 }}>
            <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 16 }}>
              <legend className="wc-kicker" style={{ marginBottom: 4 }}>
                Account-Informationen
              </legend>

              {renderField({ name: 'email', label: 'E-Mail-Adresse', type: 'email', required: true, autoComplete: 'email' })}

              <div className="wc-grid-2" style={{ gap: 16 }}>
                {renderField({ name: 'password', label: 'Passwort', type: 'password', required: true, autoComplete: 'new-password' })}
                {renderField({ name: 'password_confirm', label: 'Passwort bestätigen', type: 'password', required: true, autoComplete: 'new-password' })}
              </div>
            </fieldset>

            <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 16 }}>
              <legend className="wc-kicker" style={{ marginBottom: 4 }}>
                Firmendaten
              </legend>

              {renderField({ name: 'company_name', label: 'Firmenname', required: true, autoComplete: 'organization' })}
              {renderField({ name: 'contact_person', label: 'Ansprechpartner', required: true, autoComplete: 'name' })}
              {renderField({ name: 'phone', label: 'Telefon', type: 'tel', autoComplete: 'tel' })}
              {renderField({ name: 'address', label: 'Adresse', autoComplete: 'street-address' })}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: 16,
                }}
              >
                {renderField({ name: 'postal_code', label: 'PLZ', autoComplete: 'postal-code' })}
                {renderField({ name: 'city', label: 'Stadt', autoComplete: 'address-level2' })}
                {renderField({ name: 'country', label: 'Land', autoComplete: 'country-name' })}
              </div>
            </fieldset>

            <div>
              <label className="wc-check" style={{ alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={formData.accept_terms}
                  onChange={(e) => setField('accept_terms', e.target.checked)}
                  required
                  style={{ marginTop: 2 }}
                />
                <span style={{ fontSize: 14 }}>
                  Ich akzeptiere die{' '}
                  <a href="/agb" target="_blank" rel="noopener noreferrer">AGB</a>
                  {' '}und{' '}
                  <a href="/datenschutz" target="_blank" rel="noopener noreferrer">
                    Datenschutzerklärung
                  </a>
                  . *
                </span>
              </label>
              {validationErrors.accept_terms && (
                <p style={{ color: '#fca5a5', fontSize: 12, margin: '6px 0 0' }}>
                  {validationErrors.accept_terms}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="wc-btn wc-btn-primary wc-btn-block wc-btn-lg"
            >
              {loading ? 'Wird verarbeitet…' : 'Zahlungspflichtig bestellen'}
            </button>
          </form>
        </div>

        {/* Bestellübersicht */}
        <aside
          className="wc-card wc-elev-sm"
          style={{ padding: 24, gap: 20, position: 'sticky', top: 96 }}
        >
          <h2 style={{ fontSize: 20, margin: 0 }}>Bestellübersicht</h2>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <AppIcon app={app} size={44} />
            <div>
              <div style={{ fontSize: 16 }}>{app.name}</div>
              <div className="wc-muted" style={{ fontSize: 13 }}>{plan.name}</div>
            </div>
          </div>

          {plan.feature_list && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {parseFeatureList(plan.feature_list).map((feature, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--wc-accent)"
                    strokeWidth="2"
                    style={{ flex: 'none', marginTop: 2 }}
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span style={{ opacity: 0.85 }}>{feature}</span>
                </div>
              ))}
            </div>
          )}

          <div className="wc-hr" style={{ margin: 0 }}></div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span className="wc-muted">Abrechnungszeitraum</span>
            <span>{billingCycle === 'YEARLY' ? 'Jährlich' : 'Monatlich'}</span>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 12,
            }}
          >
            <span>Gesamtpreis</span>
            <span style={{ textAlign: 'right' }}>
              <span style={{ fontSize: 26, color: 'var(--wc-accent)' }}>
                {formatEuro(price)}
              </span>
              <span className="wc-muted" style={{ fontSize: 12, display: 'block' }}>
                / {billingCycle === 'YEARLY' ? 'Jahr' : 'Monat'}
              </span>
            </span>
          </div>

          {billingCycle === 'YEARLY' && savings > 0 && (
            <div className="wc-alert wc-alert-info">
              Sie sparen {formatEuro(savings)} ({savingsPercent} %) mit der jährlichen Zahlung.
            </div>
          )}

          <div className="wc-hr" style={{ margin: 0 }}></div>

          <p className="wc-muted" style={{ fontSize: 12, margin: 0 }}>
            Nach der Bestellung wird Ihr Account automatisch erstellt und Sie
            erhalten Zugriff auf {app.name}. Das Abonnement verlängert sich
            automatisch, kann aber jederzeit gekündigt werden.
          </p>
        </aside>
      </div>
    </div>
  )
}
